import React, { useEffect, useRef, useState } from "react";
import { CloseOutlined, DownOutlined, InfoCircleOutlined } from '@ant-design/icons';
import parse from 'html-react-parser';
import data from './benefits.json';
import assets from './assets';
import './style.scss';

function BenefitTable({ rows, label, onDetail, expanded = false }) {
  return (
    <div className={`wanda-table-scroll${expanded ? ' wanda-table-scroll--expanded' : ''}`} role="region" aria-label={`${label}对比表`} tabIndex="0">
      <table className="wanda-table" aria-label={label}>
        <colgroup><col className="wanda-benefit-column" />{data.cards.map(card => <col key={card.name} />)}</colgroup>
        <thead><tr><th scope="col"><span className="wanda-sr-only">会员礼遇</span></th>{data.cards.map(card => <th scope="col" key={card.name}>{card.name}会员</th>)}</tr></thead>
        <tbody>{rows.map(row => (
          <tr key={row.title}>
            <th scope="row">{onDetail ? <button type="button" onClick={() => onDetail(row)} aria-haspopup="dialog">{row.title}<InfoCircleOutlined aria-hidden="true" /></button> : row.title}</th>
            {row.values.map((value, index) => <td key={index} aria-label={`${data.cards[index].name}会员：${value === true ? '享有礼遇' : value || '不适用'}`}>
              {value === true ? <img src={assets[`icon-member-card${index + 1}-block.png`]} alt="享有礼遇" width="12" height="12" /> : value || <span className="wanda-sr-only">不适用</span>}
            </td>)}
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function TermsButtons({ onOpen }) {
  return data.terms.map((term, index) => <button type="button" key={term.title} onClick={() => onOpen(index)} aria-haspopup="dialog">《{term.title}》</button>);
}

function DetailDialog({ detail, onClose, rootRef }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const bodyRef = useRef(null);

  useEffect(() => {
    const trigger = document.activeElement;
    const scrollRoot = rootRef.current.closest('.container') || document.body;
    const previousOverflow = scrollRoot.style.overflow;
    scrollRoot.style.overflow = 'hidden';
    closeRef.current.focus();
    return () => {
      scrollRoot.style.overflow = previousOverflow;
      if (trigger && trigger.isConnected) trigger.focus({ preventScroll: true });
    };
  }, [rootRef]);

  const onKeyDown = event => {
    if (event.key === 'Escape') { event.preventDefault(); onClose(); }
    if (event.key !== 'Tab') return;
    const focusable = [...dialogRef.current.querySelectorAll('button, [tabindex="0"]')].filter(el => el.getClientRects().length);
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  };

  const onContentClick = event => {
    const anchor = event.target.closest('button[data-term-anchor]');
    if (!anchor || !bodyRef.current.contains(anchor)) return;
    const target = document.getElementById(anchor.dataset.termAnchor);
    if (target && bodyRef.current.contains(target)) {
      target.focus({ preventScroll: true });
      target.scrollIntoView({ block: 'start' });
    }
  };

  return (
    <div className="wanda-dialog-backdrop" onClick={event => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="wanda-dialog" role="dialog" aria-modal="true" aria-labelledby="wanda-detail-title" ref={dialogRef} onKeyDown={onKeyDown}>
        <header><h2 id="wanda-detail-title">{detail.title}</h2><button type="button" aria-label="关闭详情" title="关闭详情" onClick={onClose} ref={closeRef}><CloseOutlined /></button></header>
        <div className="wanda-dialog-body" ref={bodyRef} onClick={onContentClick}>
          {detail.rows ? <BenefitTable rows={detail.rows} label={detail.title} expanded /> : <div className="wanda-detail-copy">{parse(detail.html)}</div>}
        </div>
      </section>
    </div>
  );
}

export default function WandaBenefits() {
  const [cardIndex, setCardIndex] = useState(0);
  const [brandIndex, setBrandIndex] = useState(0);
  const [brandMenuOpen, setBrandMenuOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const rootRef = useRef(null);
  const tabsRef = useRef([]);
  const menuRef = useRef(null);
  const brandTriggerRef = useRef(null);
  const card = data.cards[cardIndex];
  const brand = data.brands[brandIndex];

  useEffect(() => {
    const host = rootRef.current.closest('.container');
    // Other routes set a global rem-based container width; isolate this H5 page.
    if (!host) return undefined;
    host.classList.add('wanda-benefits-host');
    return () => host.classList.remove('wanda-benefits-host');
  }, []);

  useEffect(() => {
    if (!brandMenuOpen) return;
    const closeMenu = event => {
      if (!menuRef.current.contains(event.target)) setBrandMenuOpen(false);
    };
    document.addEventListener('click', closeMenu);
    return () => document.removeEventListener('click', closeMenu);
  }, [brandMenuOpen]);

  const showTerms = index => setDetail(data.terms[index]);
  const showBenefit = row => setDetail({ title: row.title, html: data.details[row.detail].html });
  const onCardKeyDown = (event, index) => {
    let next;
    if (event.key === 'ArrowRight') next = (index + 1) % data.cards.length;
    if (event.key === 'ArrowLeft') next = (index + data.cards.length - 1) % data.cards.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = data.cards.length - 1;
    if (next === undefined) return;
    event.preventDefault();
    setCardIndex(next);
    tabsRef.current[next].focus();
  };

  return (
    <main className="wanda-benefits" ref={rootRef}>
      <h1 className="hotel-member-title">
        <img className="hotel-member-logo wanda-logo" src="https://staticfile.badazhou.com/20260922/e859423151e29a69f010a17ad4f36500.png" alt="" />
        <span>万悦会-会员礼遇</span>
      </h1>

      <div className="pt-60" aria-hidden={detail ? 'true' : undefined}>

        <section className="wanda-cards" aria-label="会员卡等级">
          <div className="wanda-card-panel" role="tabpanel" id="wanda-card-panel" aria-labelledby={`wanda-card-tab-${cardIndex}`}>
            <img className="wanda-card-image" src={assets[card.image]} alt={`${card.name}会员卡`} width="947" height="597" />
            <div className="wanda-card-intro">
              <div className="wanda-card-heading"><h2>{card.name}</h2><p>{card.qualification}</p></div>
              <ul>{card.benefits.map(benefit => <li key={benefit}>{benefit}</li>)}</ul>
            </div>
          </div>
          {/*<p className="wanda-terms-links">* 更多权益请查阅<TermsButtons onOpen={showTerms} /></p>*/}
          <div className="wanda-card-tabs" role="tablist" aria-label="会员等级">
            {data.cards.map((item, index) => <button type="button" role="tab" key={item.name} id={`wanda-card-tab-${index}`} aria-selected={cardIndex === index} aria-controls="wanda-card-panel" tabIndex={cardIndex === index ? 0 : -1} onClick={() => setCardIndex(index)} onKeyDown={event => onCardKeyDown(event, index)} ref={element => { tabsRef.current[index] = element; }}>
              <img src={assets[item.thumbnail]} alt="" width="181" height="112" /><span>{item.name}</span>
            </button>)}
          </div>
        </section>
        <section className="wanda-courtesy" aria-labelledby="wanda-courtesy-title">
          <h2 id="wanda-courtesy-title" className="wanda-section-title"><span>会员礼遇</span></h2>
          <div className="wanda-table-toolbar"><h3>基本礼遇</h3></div>
          <BenefitTable rows={data.basic} label="基本礼遇" onDetail={showBenefit} />
          <div className="wanda-brand-select" ref={menuRef} onKeyDown={event => { if (event.key === 'Escape' && brandMenuOpen) { setBrandMenuOpen(false); brandTriggerRef.current.focus(); } }}>
            <button type="button" className="wanda-brand-trigger" ref={brandTriggerRef} aria-label={`切换品牌权益，当前第${brandIndex + 1}组品牌`} aria-expanded={brandMenuOpen} aria-controls="wanda-brand-options" onClick={() => setBrandMenuOpen(!brandMenuOpen)}>
              <span className="wanda-brand-label">品牌权益<DownOutlined aria-hidden="true" /></span>
              <span className="wanda-brand-logos">{brand.logos.map((logo, index) => <img key={logo} src={assets[logo]} alt={`第${brandIndex + 1}组品牌标识${index + 1}`} />)}</span>
            </button>
            {brandMenuOpen && <div className="wanda-brand-options" id="wanda-brand-options">
              {data.brands.map((item, index) => index !== brandIndex && <button type="button" key={index} aria-label={`选择第${index + 1}组品牌权益`} onClick={() => { setBrandIndex(index); setBrandMenuOpen(false); brandTriggerRef.current.focus(); }}><span className="wanda-brand-logos">{item.alternateLogos.map((logo, logoIndex) => <img key={logo} src={assets[logo]} alt={`第${index + 1}组品牌标识${logoIndex + 1}`} />)}</span></button>)}
            </div>}
          </div>
          <div style={{height:'30px'}}></div>
          {/*<div className="wanda-table-toolbar wanda-brand-toolbar"><button type="button" title="展开品牌权益对比表" aria-label="展开品牌权益对比表" onClick={() => setDetail({ title: '品牌权益', rows: brand.rows })}><ExpandOutlined /></button></div>*/}
          <BenefitTable key={brandIndex} rows={brand.rows} label="品牌权益" onDetail={showBenefit} />
          <p className="wanda-terms-links wanda-table-note">相关礼遇视具体酒店而定。请查看<TermsButtons onOpen={showTerms} /></p>
        </section>
      </div>
      {detail && <DetailDialog detail={detail} rootRef={rootRef} onClose={() => setDetail(null)} />}
    </main>
  );
}
